/**
 * Grapple Hook System — repositioning tool with Verlet rope physics.
 *
 * Press G to fire a hook at terrain. On hit, a Verlet rope pulls the player
 * toward the anchor in ragdoll mode. On arrival, the player launches upward
 * with momentum. Existing ragdoll bounce physics handle wall/terrain collisions.
 */

import { GRAPPLE, PHYSICS, TUMBLE, WORLD } from '../constants';
import type { GameState, ActiveGrapple, RopeParticle } from '../types';
import { isPointInTerrain, getGroundY } from '../terrain/grid';

export interface GrappleResult {
  event: 'launched' | 'attached' | 'arrived' | 'missed' | 'canceled' | null;
  anchorX: number;
  anchorY: number;
}

/**
 * Main grapple update — call once per fixed update during aiming phase.
 */
export function updateGrapple(state: GameState): GrappleResult {
  const grapple = state.grapple;
  if (!grapple.active) return { event: null, anchorX: 0, anchorY: 0 };

  const active = grapple.active;

  if (active.phase === 'traveling') {
    return updateTraveling(state, active);
  } else {
    return updateRetraction(state, active);
  }
}

/**
 * Cancel an active grapple — player keeps current velocity.
 */
export function cancelGrapple(state: GameState): void {
  state.grapple.active = null;
}

/**
 * Force a grapple into the retracting phase at a given anchor point (multiplayer sync).
 * Used when the remote client's hook has attached — skip traveling, go straight to retraction.
 */
export function forceGrappleAttach(state: GameState, anchorX: number, anchorY: number): void {
  const active = state.grapple.active;
  if (!active) return;
  active.anchorX = anchorX;
  active.anchorY = anchorY;
  active.phase = 'retracting';
  active.hook = null;
  const player = state.players[active.playerIndex];
  createRope(active, player.x, player.y);
}

// --- Traveling phase ---

function updateTraveling(state: GameState, active: ActiveGrapple): GrappleResult {
  const hook = active.hook!;
  const G = GRAPPLE;

  // Apply gravity
  hook.vy += G.HOOK_GRAVITY;

  // Sub-step collision for tunnel prevention
  const stepVx = hook.vx / G.COLLISION_SUBSTEPS;
  const stepVy = hook.vy / G.COLLISION_SUBSTEPS;

  for (let s = 0; s < G.COLLISION_SUBSTEPS; s++) {
    hook.x += stepVx;
    hook.y += stepVy;
    hook.distanceTraveled += Math.sqrt(stepVx * stepVx + stepVy * stepVy);

    // Terrain hit check
    if (isPointInTerrain(hook.x, hook.y)) {
      // Anchor stays at terrain hit point (inside terrain) so the
      // "anchor destroyed" check in retraction works correctly.
      // The rope visual handles one segment inside terrain fine.
      active.anchorX = hook.x;
      active.anchorY = hook.y;
      active.phase = 'retracting';

      // Consume ammo
      state.grapple.ammo[active.playerIndex]--;

      const player = state.players[active.playerIndex];
      createRope(active, player.x, player.y);

      active.hook = null;
      return { event: 'attached', anchorX: active.anchorX, anchorY: active.anchorY };
    }
  }

  // Miss: range exceeded
  if (hook.distanceTraveled > G.MAX_RANGE) {
    state.grapple.active = null;
    return { event: 'missed', anchorX: 0, anchorY: 0 };
  }

  // Miss: out of world bounds
  if (hook.x < 0 || hook.x > WORLD.VIRTUAL_WIDTH ||
      hook.y < WORLD.CEILING_HEIGHT || hook.y > WORLD.VIRTUAL_HEIGHT + 200) {
    state.grapple.active = null;
    return { event: 'missed', anchorX: 0, anchorY: 0 };
  }

  return { event: null, anchorX: 0, anchorY: 0 };
}

// --- Verlet rope creation ---

function createRope(active: ActiveGrapple, playerX: number, playerY: number): void {
  const segments = GRAPPLE.ROPE_SEGMENTS;
  const dx = playerX - active.anchorX;
  const dy = playerY - active.anchorY;
  const totalLength = Math.sqrt(dx * dx + dy * dy);

  const particles: RopeParticle[] = [];
  for (let i = 0; i < segments; i++) {
    const t = i / (segments - 1);
    const x = active.anchorX + dx * t;
    const y = active.anchorY + dy * t;
    particles.push({
      x, y,
      prevX: x, prevY: y,
      pinned: i === 0, // anchor is pinned
    });
  }

  const restLengths: number[] = [];
  const segmentLength = totalLength / (segments - 1);
  for (let i = 0; i < segments - 1; i++) {
    restLengths.push(segmentLength);
  }

  active.rope = particles;
  active.ropeRestLengths = restLengths;
  active.initialRopeLength = totalLength;
}

// --- Retraction phase ---

function updateRetraction(state: GameState, active: ActiveGrapple): GrappleResult {
  const player = state.players[active.playerIndex];
  const G = GRAPPLE;

  // Check if anchor terrain was destroyed
  if (!isPointInTerrain(active.anchorX, active.anchorY)) {
    // Anchor destroyed — cancel grapple, player keeps velocity
    state.grapple.active = null;
    return { event: 'canceled', anchorX: active.anchorX, anchorY: active.anchorY };
  }

  // Retraction timeout — auto-cancel if player is stuck bouncing
  active.retractionTimer++;
  if (active.retractionTimer > G.MAX_RETRACTION_TIME) {
    state.grapple.active = null;
    return { event: 'canceled', anchorX: active.anchorX, anchorY: active.anchorY };
  }

  // 1. Shrink rest lengths (retraction)
  for (let i = 0; i < active.ropeRestLengths.length; i++) {
    active.ropeRestLengths[i] = Math.max(G.MIN_REST_LENGTH, active.ropeRestLengths[i] - G.RETRACT_SPEED / active.ropeRestLengths.length);
  }

  // 2. Update Verlet rope and get pull direction
  const pullDir = updateVerletRope(active, player.x, player.y);

  // 3. Apply pull force to player velocity
  player.vx += pullDir.x * G.PULL_STRENGTH;
  player.vy += pullDir.y * G.PULL_STRENGTH;

  // Clamp speed
  const speed = Math.sqrt(player.vx * player.vx + player.vy * player.vy);
  if (speed > G.MAX_PULL_SPEED) {
    const scale = G.MAX_PULL_SPEED / speed;
    player.vx *= scale;
    player.vy *= scale;
  }

  // 4. Enter/maintain ragdoll on player
  player.tumble = true;
  if (player.hitstunFrames <= 0) {
    player.hitstunFrames = TUMBLE.MIN_HITSTUN; // keep ragdoll alive during pull
  }
  player.actionState = { tag: 'tumble_hitstun', hitstun: player.hitstunFrames, armor: player.launchArmor, variant: 'knockback' };

  // 5. Check arrival
  const adx = player.x - active.anchorX;
  const ady = player.y - active.anchorY;
  const dist = Math.sqrt(adx * adx + ady * ady);
  if (dist < G.ARRIVAL_DISTANCE) {
    return launchFromAnchor(state, active);
  }

  return { event: null, anchorX: active.anchorX, anchorY: active.anchorY };
}

// --- Verlet rope simulation ---

function updateVerletRope(
  active: ActiveGrapple,
  playerX: number,
  playerY: number,
): { x: number; y: number } {
  const rope = active.rope;
  const restLengths = active.ropeRestLengths;
  const G = GRAPPLE;

  // Last particle tracks player position
  const last = rope[rope.length - 1];
  last.x = playerX;
  last.y = playerY;

  // dt² for Verlet gravity (fixed update = 1/60s)
  const dt2 = (1 / 60) * (1 / 60);

  // Integrate free particles (Verlet step)
  for (let i = 1; i < rope.length - 1; i++) {
    const p = rope[i];
    if (p.pinned) continue;

    const vx = (p.x - p.prevX) * G.ROPE_DAMPING;
    const vy = (p.y - p.prevY) * G.ROPE_DAMPING;
    p.prevX = p.x;
    p.prevY = p.y;
    p.x += vx;
    p.y += vy + G.ROPE_GRAVITY * dt2;
  }

  // Satisfy distance constraints
  for (let iter = 0; iter < G.CONSTRAINT_ITERATIONS; iter++) {
    for (let i = 0; i < restLengths.length; i++) {
      const p1 = rope[i];
      const p2 = rope[i + 1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < G.CONSTRAINT_EPSILON) continue;

      const restLength = restLengths[i];
      const diff = (restLength - dist) / dist;

      if (p1.pinned) {
        // Only move p2
        p2.x += dx * diff;
        p2.y += dy * diff;
      } else if (i + 1 === rope.length - 1) {
        // Last particle (player) — only move p1
        p1.x -= dx * diff;
        p1.y -= dy * diff;
      } else {
        // Move both equally
        const halfDiff = diff * 0.5;
        p1.x -= dx * halfDiff;
        p1.y -= dy * halfDiff;
        p2.x += dx * halfDiff;
        p2.y += dy * halfDiff;
      }
    }
  }

  // Terrain collision for free particles (push out of terrain)
  for (let i = 1; i < rope.length - 1; i++) {
    const p = rope[i];
    if (p.pinned) continue;
    if (isPointInTerrain(p.x, p.y)) {
      // Push to nearest non-terrain position (simple approach: try cardinal directions)
      const pushDist = G.TERRAIN_PUSH_DIST;
      const candidates = [
        { x: p.x, y: p.y - pushDist },
        { x: p.x, y: p.y + pushDist },
        { x: p.x - pushDist, y: p.y },
        { x: p.x + pushDist, y: p.y },
      ];
      for (const c of candidates) {
        if (!isPointInTerrain(c.x, c.y)) {
          p.x = c.x;
          p.y = c.y;
          break;
        }
      }
    }
  }

  // Pull direction: from player toward the second-to-last rope particle
  // This gives the rope-wrapped direction rather than straight-line
  const pullTarget = rope[rope.length - 2];
  const pdx = pullTarget.x - playerX;
  const pdy = pullTarget.y - playerY;
  const pdist = Math.sqrt(pdx * pdx + pdy * pdy);
  if (pdist < G.CONSTRAINT_EPSILON) return { x: 0, y: 0 };
  return { x: pdx / pdist, y: pdy / pdist };
}

// --- Anchor arrival & launch ---

function launchFromAnchor(state: GameState, active: ActiveGrapple): GrappleResult {
  const player = state.players[active.playerIndex];
  const G = GRAPPLE;

  // Calculate launch direction
  const approachVx = player.vx;

  // Upward impulse (core of the launch)
  player.vy = Math.min(G.LAUNCH_UPWARD_IMPULSE, player.vy * G.LAUNCH_VY_RETENTION);
  player.vx = approachVx * G.LAUNCH_FORWARD_MULT;

  // Ensure minimum upward launch speed
  if (player.vy > G.LAUNCH_MIN_VY) player.vy = G.LAUNCH_MIN_VY;

  // Slope correction: if vx pushes into rising terrain, redirect momentum upward.
  // Without this, grapple launches on steep slopes embed the tank in the slope —
  // the approach momentum drives it into rising ground during immunity frames,
  // then ground friction kills velocity and the ragdoll exits immediately.
  if (player.vx !== 0) {
    const aheadDir = Math.sign(player.vx);
    const probeX = active.anchorX + aheadDir * PHYSICS.ECB_RADIUS;
    const slopeRise = getGroundY(active.anchorX) - getGroundY(probeX); // positive = uphill ahead
    if (slopeRise > 0) {
      const steepness = Math.min(1, slopeRise / PHYSICS.ECB_RADIUS);
      const redirected = Math.abs(player.vx) * steepness;
      player.vy -= redirected;
      player.vx *= (1 - steepness);
    }
  }

  // Set ragdoll timer for post-launch phase
  player.tumble = true;
  const launchSpeed = Math.sqrt(player.vx * player.vx + player.vy * player.vy);
  player.hitstunFrames = Math.round(
    Math.min(TUMBLE.MAX_HITSTUN, Math.max(TUMBLE.MIN_HITSTUN, launchSpeed * TUMBLE.HITSTUN_PER_SPEED))
  );
  player.launchArmor = TUMBLE.LAUNCH_ARMOR_FRAMES;
  player.grappleLaunched = true;
  player.actionState = { tag: 'tumble_hitstun', hitstun: player.hitstunFrames, armor: player.launchArmor, variant: 'grapple' };

  const anchorX = active.anchorX;
  const anchorY = active.anchorY;

  // Clear grapple state
  state.grapple.active = null;

  return { event: 'arrived', anchorX, anchorY };
}

/**
 * Check if rope particles are touching terrain (for scrape effects).
 * Returns positions of contact points.
 */
export function getRopeTerrainContacts(state: GameState): { x: number; y: number }[] {
  const active = state.grapple.active;
  if (!active || active.phase !== 'retracting') return [];

  const contacts: { x: number; y: number }[] = [];
  for (let i = 1; i < active.rope.length - 1; i++) {
    const p = active.rope[i];
    // Check nearby terrain
    if (isPointInTerrain(p.x, p.y + 2) || isPointInTerrain(p.x, p.y - 2) ||
        isPointInTerrain(p.x + 2, p.y) || isPointInTerrain(p.x - 2, p.y)) {
      contacts.push({ x: p.x, y: p.y });
    }
  }
  return contacts;
}
